/**
 * Pagination configuration for patient data fetching
 * 
 * This configuration controls how patient data is fetched from the backend
 * using the paginated endpoint.
 * 
 * CURRENT SETUP: Optimized for large datasets (33,000+ patients)
 * - 1000 patients per page = 33 requests for 33k patients
 * - ~2 minutes total load time
 * - First 1000 patients in ~10 seconds
 */
export const PAGINATION_CONFIG = {
    // Default page size for patient fetching (number of patients per request)
    // Set to 1000 for optimal performance with large datasets (33,000 patients)
    DEFAULT_PAGE_SIZE: 1000,
    
    // Maximum page size (backend may have limits)
    MAX_PAGE_SIZE: 1000,
    
    // Minimum page size
    MIN_PAGE_SIZE: 10,
    
    // Use slim mode for initial loads (faster, less data)
    // When true, only essential patient fields are fetched initially
    USE_SLIM_MODE_INITIAL: false,
    
    // Safety limit to prevent infinite pagination loops
    // If we reach this many patients, stop fetching
    // Increased to 50,000 to handle large datasets
    MAX_TOTAL_PATIENTS: 50000,
    
    // Enable pagination for full syncs (when lastUpdatePatients = 0)
    // When false, always uses legacy single-request behavior
    ENABLE_PAGINATION: true,
    
    // Disable pagination for incremental syncs (when lastUpdatePatients > 0)
    // This makes sense since incremental syncs typically have few new patients
    DISABLE_PAGINATION_FOR_INCREMENTAL: true,
    
    // Add small pauses between batches to prevent overwhelming server/browser
    // Pause duration in milliseconds (0 = no pause)
    PAUSE_BETWEEN_BATCHES_MS: 0,
    
    // Show detailed progress logs every N patients
    PROGRESS_LOG_INTERVAL: 5000,
};

/**
 * Get optimal page size based on network conditions
 * Uses the Network Information API if available
 * 
 * @returns {number} Optimal page size for current network conditions
 */
export function getOptimalPageSize() {
    // Check if Network Information API is available
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    
    if (connection && connection.effectiveType) {
        // 2G or slow-2g: use smaller pages (25 patients)
        if (connection.effectiveType === '2g' || connection.effectiveType === 'slow-2g') {
            console.log('📶 Slow network detected (2G), using page size: 25');
            return 25;
        }
        // 3G: medium pages (40 patients)
        if (connection.effectiveType === '3g') {
            console.log('📶 Medium network detected (3G), using page size: 40');
            return 40;
        }
        // 4G: default page size
        console.log('📶 Fast network detected (4G+), using page size:', PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);
    }
    
    // Unknown connection or 4G+: use default
    return PAGINATION_CONFIG.DEFAULT_PAGE_SIZE;
}

/**
 * Determine if pagination should be used for a given sync
 * 
 * @param {number} lastUpdateTimestamp - Last patient update timestamp
 * @param {object} options - Optional configuration
 * @returns {boolean} True if pagination should be used
 */
export function shouldUsePagination(lastUpdateTimestamp, options = {}) {
    // Check if pagination is globally disabled
    if (!PAGINATION_CONFIG.ENABLE_PAGINATION) {
        console.log('❌ Pagination globally disabled');
        return false;
    }
    
    // Check if explicitly disabled in options
    if (options.usePagination === false) {
        console.log('❌ Pagination disabled by options');
        return false;
    }
    
    // Don't paginate if offline
    if (!navigator.onLine) {
        console.log('❌ Offline mode, skipping pagination');
        return false;
    }
    
    // Don't paginate incremental syncs (they typically have few new patients)
    if (PAGINATION_CONFIG.DISABLE_PAGINATION_FOR_INCREMENTAL && lastUpdateTimestamp > 0) {
        console.log('❌ Incremental sync detected, skipping pagination');
        return false;
    }
    
    // Use pagination for full syncs (lastUpdateTimestamp = 0)
    console.log('✅ Using pagination for full sync');
    return true;
}

/**
 * Calculate estimated pages based on total patients
 * 
 * @param {number} totalPatients - Total number of patients
 * @param {number} pageSize - Page size
 * @returns {number} Estimated number of pages
 */
export function calculateTotalPages(totalPatients, pageSize = PAGINATION_CONFIG.DEFAULT_PAGE_SIZE) {
    return Math.ceil(totalPatients / pageSize);
}

/**
 * Format progress message for logging
 * 
 * @param {number} loaded - Number of patients loaded
 * @param {number} total - Total number of patients (if known)
 * @param {boolean} hasMore - Whether more patients exist
 * @returns {string} Formatted progress message
 */
export function formatProgressMessage(loaded, total = null, hasMore = false) {
    if (total !== null) {
        const percentage = Math.round((loaded / total) * 100);
        return `${loaded}/${total} patients loaded (${percentage}%)`;
    }
    
    if (hasMore) {
        return `${loaded} patients loaded, more available...`;
    }
    
    return `${loaded} patients loaded (complete)`;
}

